<template lang="pug">
div
	h1 {{contentpage.name}}
	ws-content(:value="contentpage.content")
		client-only
			mapbox.mb-4(ref='map' style="height:400px" access-token='pk.eyJ1Ijoid2ludGVyc3BvcnQiLCJhIjoiY2psZ2kyaWlqMTRiazNrbXQ4YXZjOTU4bSJ9.A4ba6JRQOp46wohr7UICjg'
			:layers="layers"
			:sources="sources"
			:fitbounds="markers.features.map(e=>e.geometry.coordinates)"
			@map-styleload="mapStyle"
			@map-init="addGeocoder")
				div(slot='popup')
					span.h3 {{popupData.name}}
			v-row.sorting.my-4.hidden-xs-only(tag="nav")
				v-spacer
				areas-sort(v-model="sort")
					li(field="reviews.average") {{$t('localdestination-list-item.sort.rating')}}
					li(field="longestPisteLength") {{$t('localdestination-list-item.sort.longestpiste')}}
		v-data-iterator(:items="localdestinations_with_location" hide-default-footer disable-pagination :sortBy="sort.field" :sortDesc="!sort.ascending")
			template(#item="{item}")
				localdestination-list-item(v-bind="item" type="dryslope")
				v-divider.my-3
</template>

<style lang="sass">
@import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css'

.sorting
	border-bottom: 1px solid var(--v-border-base)
	overflow: auto

	a
		font-weight: 400

		&.active
			font-weight: 600

			&::after
				font-family: 'ws-icons'
				padding-right: 0
				vertical-align: middle
				content: $ws-var-caret-up
				line-height: 16px

			&.desc::after
				content: $ws-var-caret-down

			&.asc::after
				content: $ws-var-caret-up
</style>

<script>
import gql from 'graphql-tag';
import { ref } from '@vue/composition-api';
import Content from '@dengl/common/components/content.vue';
import LocaldestinationListItem from '@/components/localdestinations/list-item.js';
import LocaldestinationListMixin from '@/components/localdestinations/list-mixin.js';
import PisteTable from '@/components/localdestinations/piste-table.vue';
import MetaFragment from '@/components/graphql/metaFragment.graphql';
import useMap from '@/use/map';
import GeocoderMixin from '@/mixins/mapbox-geocoder.js';
import AreasSort from '@/components/areas/sort';

const query = gql`
	query getLocaldestinationindexpage($slug: String!) {
		contentpage(slug: $slug) {
			id
			name
			slug
			content {
				...ContentDataFragment
			}
			...MetaFragment
		}
		localdestinations: dryslopes {
			name
			slug
			logo
			affiliateUrl
			loipeLength
			tbar
			magiccarpet
			photos {
				url
				alt
			}
			longestPisteLength
			pistes {
				name
				color
				width
				length
			}
			address {
				city
				street
				phone
				postcode
				countrycode
				country
				url
			}
			tags
			content {
				introduction(length: 180)
			}
			geography {
				location {
					type
					coordinates
				}
			}
			reviews {
				voteCount
				average
			}
		}
	}
	${MetaFragment}
	${Content.fragments}
`;

export default {
	name: 'LocalDestinationDryslopesIndex',
	components: {
		LocaldestinationListItem,
		PisteTable,
		AreasSort,
		Mapbox: () => (process.client ? import('@/components/mapbox.vue') : null)
	},
	mixins: [LocaldestinationListMixin, GeocoderMixin],
	nuxtI18n: {
		paths: {
			'nl-NL': '/outdoorbanen',
			'nl-BE': '/outdoorbanen'
		}
	},
	setup() {
		const destinations = ref([]);
		const { markers, sources, attachListeners, popupData } =
			useMap(destinations);

		const layers = [
			{
				id: 'destination-markers-icons',
				type: 'symbol',
				source: 'geojson-markers',
				layout: {
					'icon-image': ['get', 'icon'],
					'icon-size': 0.3,
					'icon-anchor': 'bottom',
					'icon-allow-overlap': true
				},
				minzoom: 0,
				maxzoom: 24
			}
		];

		return {
			destinations,
			layers,
			sources,
			markers,
			attachListeners,
			popupData
		};
	},
	asyncData({ route, app }) {
		app.ads({ navigation: { visible: true } });
		const variables = {
			slug: route.path.split('/').pop()
		};
		return app.$graphClient
			.query({
				query,
				variables,
				errorPolicy: 'all'
			})
			.then(({ data, errors }) => {
				if (errors) {
					throw errors[0];
				}
				app.meta(data.contentpage.meta);
				app.breadcrumbs({
					'Wintersport in de buurt': { name: 'localdestinations' },
					'Outdoor banen': { name: 'localdestinations-dryslopes' }
				});
				return data;
			})
			.catch(error => {
				return app.httpError(
					500,
					null,
					'localdestinations-dryslopes-index-01',
					error
				);
			});
	},
	data() {
		return {
			sort: {
				ascending: false,
				field: 'reviews.average'
			},
			showMapIcons: false
		};
	},
	head() {
		return {
			...this.$structuredData()
		};
	},
	computed: {
		longestPiste() {
			return Math.max(
				...this.localdestinations_with_location.map(p => p.longestPisteLength),
				0
			);
		}
	},
	mounted() {
		this.destinations = this.localdestinations;
		if (this.$refs.map) {
			this.$nextTick(() => {
				this.$refs.map.fitToCurrentBounds();
			});
		}
	},
	methods: {
		mouseClick(feature) {
			this.$router.push(
				this.localePath({
					name: 'localdestinations-dryslopes-slug',
					params: { slug: feature.properties.slug }
				})
			);
		},
		mapStyle(map) {
			this.$refs.map.fitToCurrentBounds();
			this.attachListeners(
				map,
				this.$refs.map,
				'destination-markers-icons',
				'geojson-markers',
				this.mouseClick
			);
		}
	}
};
</script>
